/**
 * @fileoverview Get all the data for a mission.
 */
import React, { useState, useEffect } from 'react'
import {
  getMissionViewDataById,
  revertMissionDisqualification,
  updateMission as updateMissionGQL,
} from './../../Module/graphQL'
import { dropTimeZoneFromDate } from './../../shared/libraries/utils/date'
import View from './View'
import { authorization } from 'Module/Auth/Auth'

import {
  MISSION_STATUS_ENUM,
  MISSION_ENUM_TYPES,
  LOCATION_STATUS_ENUM,
} from './../../shared/constants'
import { getNumberOfHouseholdsAndDensityForMissions } from '../../Utils/Mission'
import { apiClient } from '../../Module/API/Client'
import { useDispatch } from 'react-redux'
import { hideReduxModal, showReduxModal } from '../../Reducers/UI/actions'
import { callPredictionApi } from './gql'
import { getTimeOfDayAndDayStringFromMissionDate } from '../City/Page/Ambasadors/Availabilities/utils'

const Container = props => {
  const { children, ...otherProps } = props

  const [missionData, setMissionData] = useState(null)
  const [isCityManager, setIsCityManager] = useState(false)
  const [isClientInUSAorCa, setIsClientInUSAorCa] = useState(false)

  // Will be set to true once the mission has been requested
  const [requested, setRequested] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const dispatch = useDispatch()

  var updateMission = async data => {
    try {
      // If the BA is changed, we must null out the pickup time
      if (data.userId !== undefined) data.pickupTime = null
      // If the mission is under review, now set it to completed to emulate
      // existing behaviour
      if (missionData.mission.status == MISSION_STATUS_ENUM.UNDER_REVIEW) {
        data.status = MISSION_STATUS_ENUM.COMPLETED
      }
      data.isNotShown = false
      var response = await updateMissionGQL({ missionInput: data })
      var result = await response.json()

      return result
    } finally {
      // Update mission status for the TimeLine component
      await loadMissionData()
    }
  }
  /**
   * Loads the required mission data and puts it in component state.
   */
  var loadMissionData = async function() {
    const includeLogs = true
    var response = await getMissionViewDataById(props.missionId, includeLogs)
    var result = await response.json()
    let { mission } = result.data
    let cvrPrediction = null

    if (mission.type === MISSION_ENUM_TYPES.HANDTOHAND) {
      try {
        const { timeOfDay } = getTimeOfDayAndDayStringFromMissionDate({
          missionDate: mission.timeRangeStart,
          cityCheckInTimes: mission.cityPlan.city,
        })

        const timeOfDayMap = {
          'morning': '1',
          'lunch': '2',
          'afternoon': '3',
        }

        const cvrPredictionResult = await callPredictionApi({
          city_name: mission.cityPlan.city.name,
          locations: [
            {
              location_id: mission.location.id,
              location_type_id: mission.location.typeId,
              longitude: mission.location.longitude,
              latitude: mission.location.latitude,
              date: mission.timeRangeStart.slice(0, 10),
              timeofday: timeOfDayMap[timeOfDay.toLowerCase()],
              client_id: mission.client.id,
              metric_name: 'conversion_rate',
              checkout_flyer_quantity: mission.totalFlyersDistributed || 600,
            },
          ],
        })

        cvrPrediction = cvrPredictionResult && cvrPredictionResult[0].score
        mission = {
          ...mission,
          cvrPrediction,
        }
      } catch (error) {
        console.error(error)
      }
    }

    if (mission.type === MISSION_ENUM_TYPES.LETTERBOX) {
      // We don't want to block viewing/editing mission because of area info
      try {
        const numberOfHouseholdsAndDensityByMissionId = await getNumberOfHouseholdsAndDensityForMissions(
          {
            missions: [mission],
            countryCode: mission.cityPlan.city.country.code,
          },
        )

        const {
          householdsAmount,
          density,
        } = numberOfHouseholdsAndDensityByMissionId[mission.id]

        mission = {
          ...mission,
          householdsAmount,
          density,
        }
      } catch (error) {
        mission = {
          ...mission,
          householdsAmount: '-',
          density: '-',
        }
        console.error(error)
      }
    }
    setMissionData(formatGraphQLResultIntoMissionData(mission))
    setLoaded(true)
  }

  useEffect(() => {
    if (requested) return
    setRequested(true)
    loadMissionData()
    setIsCityManager(authorization.isCityManager())
    setIsClientInUSAorCa(authorization.isClientInUSAorCA())
  })

  const onDeleteMission = async id => {
    const payload = {
      unbookReason: undefined,
      missions: [{ id }],
    }

    await apiClient.deleteMissions(payload)
    props.history.push(
      `/dashboard/campaigns/${missionData.mission.campaignId}/city-plans`,
    )
  }

  if (!missionData) return 'Loading...'

  const onUnbook = ({ cancel }) =>
    setMissionData(data =>
      cancel
        ? {
            ...data,
            mission: {
              ...data.mission,
              status: MISSION_STATUS_ENUM.CANCELLED,
            },
          }
        : {
            ...data,
            mission: {
              ...data.mission,
              status: MISSION_STATUS_ENUM.AVAILABLE,
              userId: null,
            },
            user: null,
          },
    )

  const onUndoDisqualification = () => {
    dispatch(
      showReduxModal({
        title: 'Revert Disqualified Conversions',
        children:
          'This will reinstate bonus payments for all disqualified conversions from the mission and include them in the next invoice generation. Are you sure you want to proceed?',
        onConfirm: async () => {
          try {
            await revertMissionDisqualification({ id: props.missionId })
            setMissionData({
              ...missionData,
              mission: { ...missionData.mission, bonusDisqualified: false },
            })
            setRequested(true)
            loadMissionData()
            dispatch(hideReduxModal())
          } catch (error) {
            dispatch(hideReduxModal())
          }
        },
        onCancel: () => {
          dispatch(hideReduxModal())
        },
      }),
    )
  }

  return (
    <View
      {...props}
      {...missionData}
      missionDataLoaded={loaded}
      updateMission={updateMission}
      dispatch={props.dispatch}
      // To be compatible with some legacy code
      match={otherProps.match}
      history={otherProps.history}
      deprecatedLocation={otherProps.deprecatedLocation}
      editMode={otherProps.editMode}
      onDeleteMission={onDeleteMission}
      onUnbook={onUnbook}
      onUndoDisqualification={onUndoDisqualification}
      deprecatedMission={{
        ...missionData,
        ...missionData.mission,
      }}
      isCityManager={isCityManager}
      isClientInUSAorCA={isClientInUSAorCa}
    />
  )
}

export default Container

/**
 * Sort objects on a specific prop
 */
const sortOnProp = prop => (a, b) => {
  if (a[prop] < b[prop]) return -1
  if (a[prop] > b[prop]) return 1
  return 0
}

/**
 * Converts the graphQL mission data response into the format we want for
 * rendering on the component.
 * @param {object} data
 * @returns {object}
 */
const formatGraphQLResultIntoMissionData = function(data) {
  var {
    user,
    client,
    campaign,
    location,
    cityPlan,
    conversions,
    flyerCodeV2,
    flyerCodes,
    activities = { edges: [] },
    ...mission
  } = data
  var missionData = {
    user,
    location, // Needed for the map
    flyerCodes: flyerCodes.edges.map(e => e.node),
    mission,
    locations: cityPlan.city.locations.edges.reduce((acc, { node }) => {
      if (node.status === LOCATION_STATUS_ENUM.ENABLED) {
        acc.push({
          id: node.id,
          name: node.name,
          latitude: node.latitude,
          longitude: node.longitude,
          isSelfServe: node.isSelfServe,
        })
      }

      return acc
    }, []),
    brandAmbassadors: cityPlan.city.brandAmbassadors.edges.map(({ node }) => ({
      id: node.id,
      status: node.statusV2,
      name: node.firstName + ' ' + node.lastName,
      isTeamLeader: node.isTeamLeader,
    })),
    activities: activities.edges.map(({ node }) => ({
      // This is the format the MissionMap component expects
      datetime: node.dateTime,
      latitude: node.position.latitude,
      longitude: node.position.longitude,
    })),
    flyerCodeV2,
  }
  if (location) {
    mission.locationId = location.id
    // If the location is archived it might not be in our list of locations
    var found = missionData.locations.find(l => l.id == mission.locationId)
    if (!found)
      missionData.locations.push({
        id: location.id,
        name: location.name,
        latitude: location.latitude,
        longitude: location.longitude,
        isSelfServe: location.isSelfServe,
      })
  }
  if (flyerCodeV2) {
    mission.packSize = flyerCodeV2.quantity
    mission.flyerInventoryId = flyerCodeV2.id
    var found = missionData.flyerCodes.find(
      f => f.id == mission.flyerInventoryId,
    )
    if (!found) missionData.flyerCodes.push(flyerCodeV2)
  }
  missionData.flyerCodes.sort(sortOnProp('code'))
  if (user) {
    mission.userId = user.id
    // If the BA is blocked/deleted it might not be in our list of bas
    var found = missionData.brandAmbassadors.find(b => b.id == mission.userId)
    if (!found)
      missionData.brandAmbassadors.push({
        id: user.id,
        name: user.firstName + ' ' + user.lastName,
      })
  }
  missionData.locations.sort(sortOnProp('name'))
  missionData.brandAmbassadors.sort(sortOnProp('name'))
  mission.clientId = client.id
  mission.campaignId = campaign.id
  // NOTE: I don't think this scenario should be possible but the previous code
  // checked if it existed
  if (!cityPlan) throw new Error('Missing is missing a city plan.')
  mission.cityPlanId = cityPlan.id
  mission.cityPlan = cityPlan
  missionData.city = {
    id: cityPlan.city.id,
    name: cityPlan.city.name,
    timezone: cityPlan.city.timezone,
    morningCheckInTime: cityPlan.city.morningCheckInTime,
    lunchCheckInTime: cityPlan.city.lunchCheckInTime,
    afternoonCheckInTime: cityPlan.city.afternoonCheckInTime,
    h2hCheckInTimeBefore: cityPlan.city.h2hCheckInTimeBefore,
    h2hCheckInTimeAfter: cityPlan.city.h2hCheckInTimeAfter,
    h2hBonusCheckInTimeBefore: cityPlan.city.h2hBonusCheckInTimeBefore,
    h2hBonusCheckInTimeAfter: cityPlan.city.h2hBonusCheckInTimeAfter,
    d2dCheckInTimeBefore: cityPlan.city.d2dCheckInTimeBefore,
    d2dCheckInTimeAfter: cityPlan.city.d2dCheckInTimeAfter,
    d2dBonusCheckInTimeBefore: cityPlan.city.d2dBonusCheckInTimeBefore,
    d2dBonusCheckInTimeAfter: cityPlan.city.d2dBonusCheckInTimeAfter,
    currencySign: cityPlan.city.country.currencySign,
    teamLeaderEnabled: cityPlan.city.teamLeaderEnabled,
  }
  // Ensure the mission date is a local date
  mission.dateTime = dropTimeZoneFromDate(mission.dateTime)
  mission.checkInTime = mission.checkInTime
    ? dropTimeZoneFromDate(mission.checkInTime)
    : mission.checkInTime
  mission.checkoutTime = mission.checkoutTime
    ? dropTimeZoneFromDate(mission.checkoutTime)
    : mission.checkoutTime
  missionData.conversions = conversions.nodes.map(conversion => ({
    ...conversion,
    metric: {
      ...conversion.metric,
      name: conversion.metric.label
        ? conversion.metric.label
        : conversion.metric.name,
    },
  }))
  missionData.client = client
  missionData.location = location
  missionData.campaign = campaign
  return missionData
}
